import axios from 'axios'
import { debounce } from 'lodash'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { Tooltip } from 'react-tooltip'
import { KTSVG } from '../../../../_metronic/helpers'
import { PageTitle } from '../../../../_metronic/layout/core'
import DateRangeComp from '../../../../components/dateComponent/DateRangeComp'
import { LoadingComponent } from '../../../../components/loadingComponent/LoadingComponent'
import NoRecordFound from '../../../../components/noRecord/NoRecordFound'
import RefreshComponent from '../../../../components/refreshComponent/RefreshComponent'
import { selectAuth } from '../../../../features/authSlice'
import { useAppSelector } from '../../../../hooks'
import { useGetSSToBCSyncFeedsReportQuery } from '../../../../services/ReportAndAnalyticsApi'
import tosterKey from '../../../localization/toster.json'
import SSToBCSyncFeedsReportDetailModal from './SSToBCSyncFeedsReportDetailModal'

interface SSToBCSyncFeedsProps {
  removeRoundedTop?: boolean
  showImportButtons?: boolean
}

interface QueryParams {
  startDate?: string
  endDate?: string
}

const SSToBCSyncFeeds = ({
  removeRoundedTop = false,
  showImportButtons = false,
}: SSToBCSyncFeedsProps) => {
  const { token, baseURL, userInOrgId } = useAppSelector(selectAuth)

  // Date range filter states - default to last 7 days
  const currentDate = new Date()
  const sevenDaysAgo = new Date()
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US')
  }

  const [filterByStartDate, setFilterByStartDate] = useState(formatDate(sevenDaysAgo))
  const [filterByEndDate, setFilterByEndDate] = useState(formatDate(currentDate))
  const [localFilterByStartDate, setLocalFilterByStartDate] = useState(formatDate(sevenDaysAgo))
  const [localFilterByEndDate, setLocalFilterByEndDate] = useState(formatDate(currentDate))
  const [isApplyingFilters, setIsApplyingFilters] = useState(false)

  // Detail Modal states
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [selectedReportType, setSelectedReportType] = useState<string>('')

  // Build query params object - only include non-empty values
  const getQueryParams = (): QueryParams => {
    const params: QueryParams = {}

    if (filterByStartDate && filterByStartDate !== '') {
      params.startDate = filterByStartDate
    }
    if (filterByEndDate && filterByEndDate !== '') {
      params.endDate = filterByEndDate
    }

    return params
  }

  // API call with parameters
  const {
    data,
    isFetching: isLoading,
    isSuccess,
    refetch,
  } = useGetSSToBCSyncFeedsReportQuery(getQueryParams())

  const handleDateRangeChange = (event: any, picker: any) => {
    const startDateFormatted = new Date(picker.startDate).toLocaleDateString('en-US')
    const endDateFormatted = new Date(picker.endDate).toLocaleDateString('en-US')
    setLocalFilterByStartDate(startDateFormatted)
    setLocalFilterByEndDate(endDateFormatted)
  }

  const handleCancelDateSelection = (event: any, picker: any) => {
    setLocalFilterByStartDate('')
    setLocalFilterByEndDate('')
  }

  const applyFilters = () => {
    setIsApplyingFilters(true)
    setFilterByStartDate(localFilterByStartDate)
    setFilterByEndDate(localFilterByEndDate)
  }

  const resetFilters = () => {
    const sevenDaysAgoDate = new Date()
    sevenDaysAgoDate.setDate(sevenDaysAgoDate.getDate() - 7)
    const formattedSevenDaysAgo = formatDate(sevenDaysAgoDate)
    const formattedCurrentDate = formatDate(new Date())

    setFilterByStartDate(formattedSevenDaysAgo)
    setFilterByEndDate(formattedCurrentDate)
    setLocalFilterByStartDate(formattedSevenDaysAgo)
    setLocalFilterByEndDate(formattedCurrentDate)
  }

  // Refetch when filters change
  useEffect(() => {
    if (isApplyingFilters) {
      refetch().finally(() => {
        setIsApplyingFilters(false)
      })
    }
  }, [filterByStartDate, filterByEndDate, isApplyingFilters, refetch])

  const handleRefresh = () => {
    refetch().then(() => {
      if (isSuccess) {
        toast.success(tosterKey.dataRefreshedSuccesfully)
      }
    })
  }

  // Handle view detail click - just open the modal with params
  const handleViewDetail = (reportType: string) => {
    setSelectedReportType(reportType)
    setShowDetailModal(true)
  }

  const handleCloseDetailModal = () => {
    setShowDetailModal(false)
    setSelectedReportType('')
  }

  // Format currency
  const formatCurrency = (amount: number) => {
    if (amount === undefined || amount === null) return '$ 0.00'
    const formatted = amount.toFixed(2)
    return `$ ${formatted}`
  }

  // Get summary data from response
  const summaryData = data?.result

  return (
    <>
      <PageTitle breadcrumbs={[]}>Automation SS To BC Sync Feeds</PageTitle>

      {/* Detail Modal - Only passes params, doesn't call API */}
      <SSToBCSyncFeedsReportDetailModal
        show={showDetailModal}
        handleClose={handleCloseDetailModal}
        initialReportType={selectedReportType}
        initialStartDate={filterByStartDate}
        initialEndDate={filterByEndDate}
      />

      <div className={`card mb-5 ${removeRoundedTop ? 'rounded-top-0' : ''}`}>
        <div className='card-body py-8'>
          {/* Filter Section */}
          <div className='row align-items-end mb-5'>
            <div className='col-md-4 col-lg-3 col-xl-3 mb-5'>
              <DateRangeComp
                label='Created Date Range'
                startDate={localFilterByStartDate || new Date()}
                endDate={localFilterByEndDate || new Date()}
                onApply={handleDateRangeChange}
                onCancel={handleCancelDateSelection}
                value={
                  localFilterByStartDate && localFilterByEndDate
                    ? `${localFilterByStartDate} - ${localFilterByEndDate}`
                    : 'MM/DD/YYYY'
                }
              />
            </div>
            <div className='col-md-4 col-lg-3 col-xl-3 mb-5'>
              <div className='d-flex align-items-end h-100 gap-3'>
                <button className='btn btn-danger' onClick={resetFilters}>
                  <i className="fas fa-undo me-2"></i>
                  Reset Filters
                </button>
                <button className='btn btn-primary' onClick={applyFilters} disabled={isApplyingFilters}>
                  {isApplyingFilters ? 'Applying...' : 'Apply Filters'}
                </button>
              </div>
            </div>
            <div className='col-md-4 col-lg-3 col-xl-2 mb-5 ms-auto'>
              <div className='d-flex align-items-end h-100 justify-content-end'>
                <RefreshComponent onClick={handleRefresh} />
              </div>
            </div>
          </div>

          {/* Summary Table */}
          <div className='row'>
            <div className='col-md-12'>
              <div className='table-responsive'>
                <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4 table-sm'>
                  <thead className='position-sticky top-0'>
                    <tr>
                      <th>Expense Status</th>
                      <th className='text-center'>Total Count</th>
                      <th className='text-end'>Purchase Invoices - Amount ($)</th>
                      <th className='text-end'>Credit Memo's - Amount ($)</th>
                      <th className='text-center'>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!isLoading && isSuccess && summaryData ? (
                      <>
                        {/* Row 1: Submitted */}
                        <tr>
                          <td>Expenses - Submitted By Users (SS)</td>
                          <td className='text-center'>{summaryData.totalSubmitted_Count || 0}</td>
                          <td className='text-end'>{formatCurrency(summaryData.totalSubmitted_Amount)}</td>
                          <td className='text-end'>{formatCurrency(summaryData.totalSubmitted_CreditMemoAmount)}</td>
                          <td className='text-center'>
                            <button
                              className='btn btn-sm btn-primary'
                              onClick={() => handleViewDetail('1')}
                              title="View Detail"
                            >
                              <i className="fas fa-eye me-1"></i>
                              View Details
                            </button>
                          </td>
                        </tr>
                        {/* Row 2: Manager Approved */}
                        <tr>
                          <td>Expenses - All Manager Approved (SS)</td>
                          <td className='text-center'>{summaryData.totalManagerApproved_Count || 0}</td>
                          <td className='text-end'>{formatCurrency(summaryData.totalManagerApproved_Amount)}</td>
                          <td className='text-end'>{formatCurrency(summaryData.totalManagerApproved_CreditMemoAmount)}</td>
                          <td className='text-center'>
                            <button
                              className='btn btn-sm btn-primary'
                              onClick={() => handleViewDetail('2')}
                              title="View Detail"
                            >
                              <i className="fas fa-eye me-1"></i>
                              View Details
                            </button>
                          </td>
                        </tr>
                        {/* Row 3: Sync Failed */}
                        <tr>
                          <td>Total Sync Failed (SS =&gt; BC)</td>
                          <td className='text-center'>{summaryData.syncFailed_Count || 0}</td>
                          <td className='text-end'>{formatCurrency(summaryData.syncFailed_Amount)}</td>
                          <td className='text-end'>{formatCurrency(summaryData.syncFailed_CreditMemoAmount)}</td>
                          <td className='text-center'>
                            <button
                              className='btn btn-sm btn-primary'
                              onClick={() => handleViewDetail('4')}
                              title="View Detail"
                            >
                              <i className="fas fa-eye me-1"></i>
                              View Details
                            </button>
                          </td>
                        </tr>
                        {/* Row 4: Pending For Sync */}
                        <tr>
                          <td>Total Pending For Sync (SS =&gt; BC)</td>
                          <td className='text-center'>{summaryData.pendingForBCSync_Count || 0}</td>
                          <td className='text-end'>{formatCurrency(summaryData.pendingForBCSync_Amount)}</td>
                          <td className='text-end'>{formatCurrency(summaryData.pendingForBCSync_CreditMemoAmount)}</td>
                          <td className='text-center'>
                            <button
                              className='btn btn-sm btn-primary'
                              onClick={() => handleViewDetail('3')}
                              title="View Detail"
                            >
                              <i className="fas fa-eye me-1"></i>
                              View Details
                            </button>
                          </td>
                        </tr>
                        {/* Row 5: Created in BC (Pending AP) */}
                        <tr>
                          <td>Created in BC (BC - Pending For AP Approval)</td>
                          <td className='text-center'>{summaryData.totalCreatedInBC_PendingAP_Count || 0}</td>
                          <td className='text-end'>{formatCurrency(summaryData.totalCreatedInBC_PendingAP_Amount)}</td>
                          <td className='text-end'>{formatCurrency(summaryData.totalCreatedInBC_PendingAP_CreditMemoAmount)}</td>
                          <td className='text-center'>
                            <button
                              className='btn btn-sm btn-primary'
                              onClick={() => handleViewDetail('5')}
                              title="View Detail"
                            >
                              <i className="fas fa-eye me-1"></i>
                              View Details
                            </button>
                          </td>
                        </tr>
                        {/* Row 6: AP Approved */}
                        <tr>
                          <td>Expenses created in BC (BC - AP Approved)</td>
                          <td className='text-center'>{summaryData.totalAPApproved_Count || 0}</td>
                          <td className='text-end'>{formatCurrency(summaryData.totalAPApproved_Amount)}</td>
                          <td className='text-end'>{formatCurrency(summaryData.totalAPApproved_CreditMemoAmount)}</td>
                          <td className='text-center'>
                            <button
                              className='btn btn-sm btn-primary'
                              onClick={() => handleViewDetail('6')}
                              title="View Detail"
                            >
                              <i className="fas fa-eye me-1"></i>
                              View Details
                            </button>
                          </td>
                        </tr>
                        {/* Row 7: Paid */}
                        <tr>
                          <td>Expenses created in BC (BC - Paid)</td>
                          <td className='text-center'>{summaryData.totalPaid_Count || 0}</td>
                          <td className='text-end'>{formatCurrency(summaryData.totalPaid_Amount)}</td>
                          <td className='text-end'>{formatCurrency(summaryData.totalPaid_CreditMemoAmount)}</td>
                          <td className='text-center'>
                            <button
                              className='btn btn-sm btn-primary'
                              onClick={() => handleViewDetail('7')}
                              title="View Detail"
                            >
                              <i className="fas fa-eye me-1"></i>
                              View Details
                            </button>
                          </td>
                        </tr>
                      </>
                    ) : isLoading ? (
                      <tr>
                        <td colSpan={5} className='text-center'>
                          <LoadingComponent />
                        </td>
                      </tr>
                    ) : (
                      <tr>
                        <td colSpan={5} className='text-center'>
                          <NoRecordFound />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default SSToBCSyncFeeds